import { useRef, useState } from 'react';
import { date, money } from '../lib/format';
import { errorMessage } from '../lib/api-error';
import {
  BANK_STATUS_LABELS,
  PAYMENT_TARGET_LABELS,
  PAYMENT_TARGET_TYPES,
  useAllocatePayment,
  useAutoMatch,
  useBankAccounts,
  useBankTransactions,
  useIgnoreTransaction,
  useImportCoda,
  type BankTransaction,
  type PaymentTargetType,
} from '../lib/banking-api';
import {
  Badge,
  Button,
  Card,
  CardHeader,
  Checkbox,
  EmptyState,
  ErrorState,
  Input,
  PageHeader,
  Select,
  Spinner,
  Table,
  Td,
  Th,
} from '../components/ui';

/**
 * BANKA — hesaplar, CODA içe aktarma, eşleştirme.
 *
 * ⚠⚠ İÇE AKTARMA DEFTERE YAZMAZ ve ekran bunu söyler. Hareketler "eşleşmedi"
 * doğar; deftere kayıt ayrı bir adımdır ve atılmış fiş geri açılmaz.
 *
 * ⚠ OTOMATİK EŞLEŞTİRME YALNIZ YAPILANDIRILMIŞ MESAJ (OGM) ÜZERİNDEN. Tutar
 * benzerliği cazip ama tehlikeli: aynı tutarlı iki fatura varsa yanlışını
 * kapatır ve yanlış kapanan bir fatura, ödenmemiş olduğu hâlde ödenmiş görünür.
 */
export function BankingPage() {
  const accounts = useBankAccounts();
  const [seciliHesap, setSeciliHesap] = useState<string>('');
  const [yalnizEslesmeyen, setYalnizEslesmeyen] = useState(true);
  const transactions = useBankTransactions(
    seciliHesap === '' ? null : seciliHesap,
    yalnizEslesmeyen,
  );
  const iceAktar = useImportCoda();
  const otomatik = useAutoMatch();
  const yoksay = useIgnoreTransaction();
  const dosyaGirisi = useRef<HTMLInputElement>(null);
  const [hata, setHata] = useState<string | null>(null);
  const [ozet, setOzet] = useState<string | null>(null);

  const hesaplar = accounts.data ?? [];
  const aktifHesap = seciliHesap === '' ? (hesaplar[0]?.id ?? '') : seciliHesap;

  return (
    <>
      <PageHeader
        title="Banka"
        description="CODA ekstreleri ve ödeme eşleştirme. İçe aktarmak, deftere yazmak değildir."
      />

      <Card className="mt-4">
        <CardHeader title="Hesaplar" />
        {accounts.isPending ? (
          <div className="px-4 py-6">
            <Spinner />
          </div>
        ) : accounts.isError ? (
          <ErrorState message={errorMessage(accounts.error)} />
        ) : hesaplar.length === 0 ? (
          <EmptyState
            title="Banka hesabı yok"
            description="CODA yükleyebilmek için önce hesabı tanımlayın: IBAN ve karşılığı olan defter hesabı (550000…)."
          />
        ) : (
          <Table>
            <thead>
              <tr>
                <Th>Ad</Th>
                <Th>IBAN</Th>
                <Th>Defter hesabı</Th>
                <Th> </Th>
              </tr>
            </thead>
            <tbody>
              {hesaplar.map((a) => (
                <tr key={a.id}>
                  <Td>{a.name}</Td>
                  <Td className="font-mono text-xs">{a.iban}</Td>
                  <Td className="font-mono text-xs">{a.ledgerAccountCode}</Td>
                  <Td>{a.isMain && <Badge tone="brand">Ana hesap</Badge>}</Td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card>

      {hesaplar.length > 0 && (
        <Card className="mt-4">
          <CardHeader
            title="CODA yükle"
            description="Bayt olarak aynı dosya ikinci kez yüklenemez; aynı ekstre farklı bir dosyadan gelirse hareketler çoğalmaz."
          />
          <div className="space-y-3 px-4 py-3">
            <Select
              label="Hesap"
              value={aktifHesap}
              onChange={(e) => setSeciliHesap(e.target.value)}
            >
              {hesaplar.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name} — {a.iban}
                </option>
              ))}
            </Select>

            {/*
              ⚠ HAM BAYT, multipart DEĞİL: multipart kullanıcı girdisinden gelen
              bir dosya adı taşır ve sunucu onu hiçbir yolda kullanmıyor.
            */}
            <input
              ref={dosyaGirisi}
              type="file"
              className="hidden"
              onChange={(e) => {
                const file = e.target.files?.[0];
                e.target.value = '';
                if (!file || aktifHesap === '') return;
                setHata(null);
                setOzet(null);
                iceAktar.mutate(
                  { accountId: aktifHesap, file },
                  {
                    onSuccess: (r) =>
                      setOzet(
                        `${String(r.statements)} ekstre · ${String(r.movements)} hareket yazıldı · ` +
                          `${String(r.skippedDetails)} detay satırı atlandı · ${String(r.duplicates)} tekrar`,
                      ),
                    onError: (err: unknown) => setHata(errorMessage(err)),
                  },
                );
              }}
            />
            <div className="flex items-center gap-2">
              <Button disabled={iceAktar.isPending} onClick={() => dosyaGirisi.current?.click()}>
                CODA dosyası seç
              </Button>
              <Button
                variant="secondary"
                disabled={otomatik.isPending}
                onClick={() => {
                  setHata(null);
                  setOzet(null);
                  otomatik.mutate(aktifHesap === '' ? undefined : aktifHesap, {
                    onSuccess: (r) =>
                      setOzet(
                        `${String(r.candidates)} aday hareketten ${String(r.matched)} tanesi eşleşti`,
                      ),
                    onError: (err: unknown) => setHata(errorMessage(err)),
                  });
                }}
              >
                Yapılandırılmış mesajla eşleştir
              </Button>
              {(iceAktar.isPending || otomatik.isPending) && <Spinner />}
            </div>

            {/*
              ⚠ ATLANAN DETAY SAYISI GÖSTERİLİYOR — "eksik geldi" sanılmasın.
              CODA'da bir toplamın detayları ayrı satırlar hâlinde gelir;
              ikisini birden yazmak banka hesabını İKİ KATINA çıkarırdı.
            */}
            {ozet && <p className="text-sm">{ozet}</p>}
            {hata && <p className="text-xs text-[var(--c-danger)]">{hata}</p>}

            <p className="rounded-[var(--r-md)] border border-[var(--c-border)] px-3 py-2 text-xs text-[var(--c-ink-soft)]">
              <strong>İçe aktarmak deftere yazmak değildir.</strong> Hareketler "eşleşmedi" olarak
              düşer; hangi belgeye ait olduklarını siz söylersiniz. Otomatik eşleştirme yalnız
              yapılandırılmış mesajı (OGM) olan tahsilatları kapatır — tutar benzerliğine bakılmaz,
              çünkü aynı tutarlı iki fatura varsa yanlışı kapanırdı.
            </p>
          </div>
        </Card>
      )}

      <Card className="mt-4">
        <CardHeader title="Hareketler" />
        <div className="border-b border-[var(--c-border)] px-4 py-3">
          <Checkbox
            label="Yalnız eşleşmeyenler"
            checked={yalnizEslesmeyen}
            onChange={(e) => setYalnizEslesmeyen(e.target.checked)}
          />
        </div>
        {transactions.isPending ? (
          <div className="px-4 py-6">
            <Spinner />
          </div>
        ) : (transactions.data ?? []).length === 0 ? (
          <EmptyState title="Hareket yok" />
        ) : (
          <Table>
            <thead>
              <tr>
                <Th>Tarih</Th>
                <Th>Karşı taraf</Th>
                <Th>Mesaj</Th>
                <Th align="right">Tutar</Th>
                <Th>Durum</Th>
                <Th> </Th>
              </tr>
            </thead>
            <tbody>
              {(transactions.data ?? []).map((tx) => (
                <TransactionRow
                  key={tx.id}
                  tx={tx}
                  onError={setHata}
                  onIgnore={() => yoksay.mutate(tx.id)}
                />
              ))}
            </tbody>
          </Table>
        )}
      </Card>
    </>
  );
}

function TransactionRow(props: {
  tx: BankTransaction;
  onError: (m: string) => void;
  onIgnore: () => void;
}) {
  const { tx } = props;
  const tahsis = useAllocatePayment();
  const [acik, setAcik] = useState(false);
  const [targetType, setTargetType] = useState<PaymentTargetType>('SALES_INVOICE');
  const [amount, setAmount] = useState(Math.abs(Number(tx.amount)).toFixed(2));
  const [targetId, setTargetId] = useState('');

  const giris = Number(tx.amount) > 0;

  return (
    <>
      <tr>
        <Td className="text-xs">{date(tx.bookingDate)}</Td>
        <Td>
          <span className="text-sm">{tx.counterpartyName ?? '—'}</span>
          <br />
          <span className="font-mono text-[11px] text-[var(--c-ink-soft)]">
            {tx.counterpartyIban ?? ''}
          </span>
        </Td>
        <Td className="text-xs">
          {/*
            ⚠ YAPILANDIRILMIŞ MESAJ AYRI GÖSTERİLİYOR: yalnız mod-97 sağlaması
            TUTAN 12 hane buraya yazılır, yani görünüyorsa doğrulanmıştır.
          */}
          {tx.structuredCommunication ? (
            <span className="font-mono">+++{tx.structuredCommunication}+++</span>
          ) : (
            (tx.communication ?? '—')
          )}
        </Td>
        {/* ⚠ İŞARET HAREKETİN ÜZERİNDE: giriş +, çıkış −. */}
        <Td align="right">
          <span className={giris ? '' : 'text-[var(--c-danger)]'}>{money(tx.amount)}</span>
        </Td>
        <Td>
          <Badge
            tone={
              tx.status === 'MATCHED'
                ? 'ok'
                : tx.status === 'IGNORED'
                  ? 'neutral'
                  : tx.status === 'PARTIALLY_MATCHED'
                    ? 'brand'
                    : 'warn'
            }
          >
            {BANK_STATUS_LABELS[tx.status]}
          </Badge>
        </Td>
        <Td>
          {tx.status !== 'MATCHED' && tx.status !== 'IGNORED' && (
            <div className="flex gap-1">
              <Button variant="ghost" onClick={() => setAcik((v) => !v)}>
                Eşleştir
              </Button>
              <Button variant="ghost" onClick={props.onIgnore}>
                Yok say
              </Button>
            </div>
          )}
        </Td>
      </tr>
      {acik && (
        <tr>
          <Td colSpan={6}>
            <div className="grid gap-2 sm:grid-cols-4">
              {/*
                ⚠ HEDEF TÜRÜ AÇIKÇA SEÇİLİR. Tahmin edilseydi yanlış kapanan bir
                fatura ödenmemiş olduğu hâlde ödenmiş görünürdü.
              */}
              <Select
                label="Ne için"
                value={targetType}
                onChange={(e) => setTargetType(e.target.value as PaymentTargetType)}
              >
                {PAYMENT_TARGET_TYPES.map((t) => (
                  <option key={t} value={t}>
                    {PAYMENT_TARGET_LABELS[t]}
                  </option>
                ))}
              </Select>
              <Input
                label="Belge kimliği"
                value={targetId}
                hint="Özel/Diğer dışında zorunlu"
                onChange={(e) => setTargetId(e.target.value)}
              />
              <Input
                label="Tutar"
                value={amount}
                hint="Hareketi aşamaz"
                onChange={(e) => setAmount(e.target.value)}
              />
              <div className="flex items-end">
                <Button
                  disabled={tahsis.isPending}
                  onClick={() =>
                    tahsis.mutate(
                      {
                        transactionId: tx.id,
                        targetType,
                        targetId: targetId === '' ? null : targetId,
                        amount,
                      },
                      {
                        onSuccess: () => setAcik(false),
                        onError: (e: unknown) => props.onError(errorMessage(e)),
                      },
                    )
                  }
                >
                  Tahsis et
                </Button>
              </div>
            </div>
          </Td>
        </tr>
      )}
    </>
  );
}
